import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { computed } from 'mobx';
import { NonBlockingNotificationType, NotificationType } from 'src_server/trpc/types/accountModel/accountResponse';
import { Common } from 'src/domains/common/Common';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import {
    buildUpdatedNotificationsOnConfirm,
    buildUpdatedNotificationsOnShown,
    findActiveNotification,
} from 'src/domains/players/state/notificationTimestamps';

export class NonBlockingNotificationsState {
    private readonly basicDataComp: BasicDataModel;

    public constructor(private readonly common: Common) {
        this.basicDataComp = BasicDataModel.get(this.common);
    }

    public static get = AutoWeakMap.create((common: Common) => new NonBlockingNotificationsState(common));

    @computed public get getNonBlockingNotificationType(): NotificationType | null {
        const customerData = this.basicDataComp.basicDataReady;
        if (customerData === null) {
            return null;
        }

        const allNotifications = customerData.prompts?.notificationList ?? [];
        const activeNotifications = [...allNotifications].filter((notification) => notification.active);

        return (
            activeNotifications.find((notification) => {
                switch (notification.name) {
                    case 'reviewAccount':
                        return NonBlockingNotificationType.REVIEW_ACCOUNT;
                }
            })?.name ?? null
        );
    }

    public handleConfirmReviewAccount = async (): Promise<void> => {
        await this.onConfirm(NonBlockingNotificationType.REVIEW_ACCOUNT);
    };

    public handleConfirmReviewLimits = async (): Promise<void> => {
        await this.onConfirm(NonBlockingNotificationType.REVIEW_LIMITS);
    };

    public markNotificationShown = async (notificationName: string): Promise<void> => {
        const hasActiveNotification =
            this.basicDataComp.basicDataReady?.prompts?.notificationList.some(
                (item) => item.name === notificationName && item.active
            ) ?? false;

        if (hasActiveNotification === false) {
            return;
        }

        await this.basicDataComp.basicData.refreshAndWait();

        const allNotifications = this.basicDataComp.basicDataReady?.prompts?.notificationList ?? [];
        const notification = findActiveNotification(allNotifications, notificationName);

        if (notification?.active !== true) {
            return;
        }

        const shownAt = new Date().toISOString();

        await this.common.trpcClient.client.accounts.approveNotification.mutate({
            prompts: {
                notificationList: buildUpdatedNotificationsOnShown(allNotifications, notificationName, shownAt),
            },
        });

        this.basicDataComp.basicData.refresh();
    };

    private onConfirm = async (nonBlockingNotificationType: NonBlockingNotificationType): Promise<void> => {
        await this.basicDataComp.basicData.refreshAndWait();

        const allNotifications = this.basicDataComp.basicDataReady?.prompts?.notificationList ?? [];

        if (findActiveNotification(allNotifications, nonBlockingNotificationType) === undefined) {
            return;
        }

        const closedAt = new Date().toISOString();

        await this.common.trpcClient.client.accounts.approveNotification.mutate({
            prompts: {
                notificationList: buildUpdatedNotificationsOnConfirm(
                    allNotifications,
                    nonBlockingNotificationType,
                    closedAt
                ),
            },
        });

        await this.basicDataComp.basicData.refreshAndWait();
    };
}
